/**
 * Token-Refresh-Wrapper fuer die react-admin fetchUtils.fetchJson-basierte
 * Pipeline (Issue #297). Analog zum frontend `apiRefreshHandler.ts`:
 *
 * - Bei 401 wird ein refresh versucht.
 * - Erfolg: Original-Request mit neuem Bearer-Header retryen.
 * - Refresh-Fail (kein Refresh-Token, abgelaufen, SSO-Fehler): Tokens
 *   bereinigt entfernen, originalen 401-Fehler durchreichen.
 * - Nicht-401 oder bereits retried: unveraendert durchreichen.
 *
 * fetchUtils.fetchJson wirft bei !ok einen Error mit `.status` und `.body`.
 * Wir fangen diesen Error, decideieren basierend auf `status`, und ggf.
 * refetchen.
 */
import { tokenService } from './tokenService';

type HttpClient = (url: string, options?: any) => Promise<any>;

/**
 * Wickelt einen httpClient so ein, dass 401-Antworten transparent ueber den
 * Refresh-Flow recovern. Marker `_retried` auf den options haengen, damit
 * ein doppelter 401 (refreshter Token taugt auch nichts) nicht in eine
 * Endlos-Loop laeuft.
 */
export function withRefreshRetry(inner: HttpClient): HttpClient {
    return async function (url: string, options: any = {}) {
        try {
            return await inner(url, options);
        } catch (err: any) {
            if (err?.status !== 401 || options._retried) {
                throw err;
            }
            const refreshed = await tokenService.refreshAccessToken();
            if (!refreshed) {
                tokenService.removeToken();
                tokenService.removeRefreshToken();
                throw err;
            }
            // Header mit neuem Bearer ueberschreiben — fetchUtils legt
            // `options.headers` als Headers-Instance ab.
            const newToken = tokenService.getToken();
            if (newToken) {
                if (!options.headers || typeof options.headers.set !== 'function') {
                    options.headers = new Headers(options.headers || {});
                }
                options.headers.set('Authorization', `Bearer ${newToken}`);
            }
            options._retried = true;
            return inner(url, options);
        }
    };
}
